/**
 * Pure utility functions for inbox tab construction and email filtering.
 * Extracted from PortalInboxPage for testability.
 */

import type { TabFilter } from '../../components/portal/FilterTabs';
import type { PortalEmailItem, InboxAlertsData } from '../../types/portal';

/**
 * Order record statuses that indicate the order has been submitted and should
 * no longer appear in the inbox "orders" category.  These emails belong on
 * the dedicated Orders page instead.
 */
const SUBMITTED_ORDER_STATUSES = new Set([
  'submitted_to_sage',
  'confirmed_in_sage',
  'accepted',
  'shipped',
  'order_completed',
  'cancelled',
]);

/** Returns true when the email's linked order has been submitted or later. */
function _isOrderSubmitted(status: string | null | undefined): boolean {
  return !!status && SUBMITTED_ORDER_STATUSES.has(status);
}

export interface TabDef {
  id: TabFilter;
  label: string;
  count: number;
  variant?: 'default' | 'urgent';
}

/**
 * Build the list of filter tabs for the inbox.
 *
 * Tab order:  My Important → All Important → Unmapped
 *
 * "My Important"  = emails where the monitoring pipeline produced a NOTIFY
 *                   or UNSURE decision AND are mapped to the current user.
 * "All Important" = same NOTIFY/UNSURE filter but UNION across every
 *                   salesperson (Emily + Robin combined).  Lets a CSR pick
 *                   up colleagues' important items in one click.
 * "Unmapped"      = emails not matched to any salesperson (separate dataset).
 *
 * The legacy "All" / "Unread" tabs were removed per the 2026-04-27 meeting:
 * the personalized inbox needs less noise, not more.  All-emails browsing
 * lives on the dedicated /portal/all-emails page; unread filtering is
 * available via the Important tab's read-state sub-filter.
 */
export function buildInboxTabs(opts: {
  importantCount: number;
  allImportantCount: number;
  unmappedCount: number;
}): TabDef[] {
  const tabs: TabDef[] = [
    { id: 'important', label: 'My Important', count: opts.importantCount },
    {
      id: 'all-important',
      label: 'All Important',
      count: opts.allImportantCount,
    },
  ];

  if (opts.unmappedCount > 0) {
    tabs.push({
      id: 'unmapped',
      label: 'Unmapped',
      count: opts.unmappedCount,
      variant: 'urgent',
    });
  }

  return tabs;
}

/**
 * Resolve the All Important tab count.  Prefers the backend stat
 * (`stats.all_important`), which counts every cross-user important email
 * — not just the loaded page.  Falls back to the client-side count of
 * loaded emails matching the same predicate when the stat isn't yet
 * available (initial load before /api/portal/inbox/stats returns).
 *
 * The predicate mirrors the server-side `view=all-important` filter:
 * NOTIFY/UNSURE decision AND not resolved.  We deliberately do NOT
 * require a linked Notification row — emails the pipeline flagged as
 * important must show even when the notification side-effect failed,
 * otherwise the rendered list undershoots the server total and the
 * pagination footer reads "Showing 7 of 37".
 */
export function resolveAllImportantCount(
  statsAllImportant: number | null | undefined,
  loadedEmails: ReadonlyArray<{
    notification_decision?: string | null;
    is_resolved?: boolean;
    notification_is_dismissed?: boolean;
  }>,
): number {
  if (typeof statsAllImportant === 'number') return statsAllImportant;
  return loadedEmails.filter(
    (e) =>
      (e.notification_decision === 'notify' ||
        e.notification_decision === 'unsure') &&
      !(e.is_resolved || e.notification_is_dismissed),
  ).length;
}

/**
 * Resolve the unmapped email count from either the loaded data
 * or the alerts count (used before unmapped emails are fetched).
 */
export function resolveUnmappedCount(
  unmappedLoaded: boolean,
  totalUnmapped: number,
  alertsData: InboxAlertsData | null,
): number {
  if (unmappedLoaded) return totalUnmapped;
  return alertsData?.unmapped_email_count ?? 0;
}

type Category = 'all' | 'new-customers' | 'orders' | 'quotes' | 'inquiries' | 'internal';

/**
 * An email is "resolved" when EITHER its action-item notification has been
 * dismissed via the "Mark as Resolved" button OR its linked OrderRecord has
 * reached a terminal status (submitted/accepted/completed/cancelled).
 *
 * Returns true if EITHER `is_resolved` (computed by the backend) OR
 * `notification_is_dismissed` (the underlying signal) is set.  ORing both
 * signals — rather than preferring one — keeps optimistic UI updates
 * working (the bulk-resolve handler flips both flags) and stays correct
 * against older API payloads that don't yet emit `is_resolved`.
 */
export function isEmailResolved(e: PortalEmailItem): boolean {
  return Boolean(e.is_resolved) || Boolean(e.notification_is_dismissed);
}

/**
 * Filter emails by active tab and category.
 * When activeTab is 'unmapped', returns the unmapped dataset directly.
 *
 * @param showResolved - When false (default) and activeTab is 'important',
 *   resolved items are excluded. When true, all important emails are shown
 *   regardless of resolved state. "Resolved" is unified per `isEmailResolved`.
 */
export function filterInboxEmails(
  emails: PortalEmailItem[],
  unmappedEmails: PortalEmailItem[],
  activeTab: TabFilter,
  category: Category,
  showResolved: boolean = false,
): PortalEmailItem[] {
  if (activeTab === 'unmapped') {
    return unmappedEmails;
  }

  let filtered = [...emails];

  // Apply category filter
  if (category !== 'all') {
    switch (category) {
      case 'new-customers':
        filtered = filtered.filter((e) => e.is_new_customer);
        break;
      case 'orders':
        filtered = filtered.filter(
          (e) =>
            (e.has_order || e.classification_category === 'order_request') &&
            !_isOrderSubmitted(e.order_record_status),
        );
        break;
      case 'quotes':
        filtered = filtered.filter(
          (e) => e.classification_category === 'quote_request',
        );
        break;
      case 'inquiries':
        filtered = filtered.filter(
          (e) =>
            e.classification_category === 'general_inquiry' ||
            (!e.has_order &&
              !e.is_new_customer &&
              !e.is_internal &&
              e.classification_category !== 'order_request' &&
              e.classification_category !== 'quote_request'),
        );
        break;
      case 'internal':
        filtered = filtered.filter((e) => e.is_internal);
        break;
    }
  }

  // Apply tab filter.  Both the My Important and All Important tabs share
  // the same client-side predicate (NOTIFY/UNSURE + unresolved); the
  // salesperson scope is enforced server-side via the chosen view
  // (`important` vs `all-important`).
  //
  // We intentionally do NOT require a linked Notification row.  Emails
  // the monitoring pipeline classified as NOTIFY/UNSURE must still surface
  // even when the notification side-effect didn't materialise (race, retry
  // gap, manual deletion) — otherwise the rendered list undershoots the
  // server total and the footer reads "Showing 7 of 37".  The Mark-as-
  // Resolved button is independently gated by `canResolveActionItem`, so
  // these "orphan" rows render as read-only action items.
  switch (activeTab) {
    case 'important':
    case 'all-important':
      filtered = filtered.filter(
        (e) =>
          (e.notification_decision === 'notify' ||
            e.notification_decision === 'unsure') &&
          (showResolved || !isEmailResolved(e)),
      );
      break;
    case 'unread':
      filtered = filtered.filter((e) => e.is_unread);
      break;
  }

  return filtered;
}
